import type { ComponentProps, ReactNode } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useMutation } from "@tanstack/react-query";
import { type } from "arktype";
import { Loader2 } from "lucide-react";
import { useForm, type UseFormRegisterReturn } from "react-hook-form";

import { orpc } from "@/client";
import { TodaySchemas } from "@/api/router/today/schemas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useActionDecision } from "@/hooks/use-action-decision";
import { ActionQueueLabels } from "@/lib/action-queue";
import { djs } from "@/lib/djs";
import type {
	ActionDecisionKind,
	ActionDecisionProps,
	ActionDecisionResult,
} from "@/types/actions";

const review = type({ review_at: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/u });

const ActionDecisionForms = {
	note: TodaySchemas.complete.omit("id", "version"),
	contest: TodaySchemas.contest.omit("id", "version"),
	contact: TodaySchemas.contact.omit("id", "version", "next_review_at").and(review),
	wait: TodaySchemas.wait.omit("id", "version", "next_review_at").and(review),
	transfer: TodaySchemas.transfer.omit("id", "version"),
};

const dialogs = {
	complete: CompleteDecision,
	contact: ContactDecision,
	wait: WaitDecision,
	transfer: TransferDecision,
	resume: ResumeDecision,
	contest: ContestDecision,
};

export function ActionDecisionDialog({
	kind,
	action,
	onClose,
}: ActionDecisionProps & { kind: ActionDecisionKind }) {
	const Dialog = dialogs[kind];

	return <Dialog action={action} onClose={onClose} />;
}

function DecisionForm({
	action,
	kind,
	onClose,
	onSubmit,
	isPending,
	isValid,
	result,
	children,
}: ActionDecisionProps & {
	kind: ActionDecisionKind;
	onSubmit: ComponentProps<"form">["onSubmit"];
	isPending: boolean;
	isValid: boolean;
	result: ActionDecisionResult | undefined;
	children: ReactNode;
}) {
	const stale =
		result?.ok === false && (result.code === "action_changed" || result.code === "action_not_open");

	return (
		<Modal
			title={ActionQueueLabels.decision[kind]}
			description={action.name}
			onClose={onClose}
			isPending={isPending}
			className="max-w-md"
		>
			<form
				data-component={`${kind}-action-dialog`}
				onSubmit={onSubmit}
				className="flex flex-col gap-4"
			>
				<p className="text-sm text-foreground">{action.task}</p>
				{children}
				{result?.ok === false && (
					<p role="alert" className="text-sm text-destructive">
						{ActionQueueLabels.error[result.code]}
					</p>
				)}
				<div className="flex justify-end gap-2">
					<Button
						type="button"
						size="sm"
						variant="secondary"
						onClick={onClose}
						disabled={isPending}
					>
						cancelar
					</Button>
					<Button size="sm" type="submit" disabled={isPending || !isValid || stale}>
						{isPending && <Loader2 className="animate-spin" />}
						{isPending && "registrando decisão..."}
						{!isPending && ActionQueueLabels.decision[kind]}
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function DecisionNote({
	registration,
	label,
	isPending,
}: {
	registration: UseFormRegisterReturn;
	label: string;
	isPending: boolean;
}) {
	return (
		<label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
			{label}
			<Textarea {...registration} aria-label={label} autoFocus rows={3} disabled={isPending} />
		</label>
	);
}

function DecisionReview({
	registration,
	isPending,
}: {
	registration: UseFormRegisterReturn;
	isPending: boolean;
}) {
	return (
		<label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
			retorno à pendência
			<Input
				{...registration}
				aria-label="retorno à pendência"
				type="datetime-local"
				min={djs().tz("America/Sao_Paulo").format("YYYY-MM-DDTHH:mm")}
				disabled={isPending}
			/>
		</label>
	);
}

function CompleteDecision({ action, onClose }: ActionDecisionProps) {
	const { mutate, isPending, data } = useMutation(
		orpc.today.complete.mutationOptions(useActionDecision(action, onClose)),
	);
	const form = useForm<typeof ActionDecisionForms.note.infer>({
		resolver: arktypeResolver(ActionDecisionForms.note),
		mode: "onChange",
		defaultValues: { note: "" },
	});

	return (
		<DecisionForm
			action={action}
			kind="complete"
			onClose={onClose}
			onSubmit={form.handleSubmit((input) =>
				mutate({ id: action.id, version: action.version, note: input.note }),
			)}
			isPending={isPending}
			isValid={form.formState.isValid}
			result={data}
		>
			<DecisionNote
				registration={form.register("note")}
				label="o que foi resolvido?"
				isPending={isPending}
			/>
		</DecisionForm>
	);
}

function ContactDecision({ action, onClose }: ActionDecisionProps) {
	const { mutate, isPending, data } = useMutation(
		orpc.today.contact.mutationOptions(useActionDecision(action, onClose)),
	);
	const form = useForm<typeof ActionDecisionForms.contact.infer>({
		resolver: arktypeResolver(ActionDecisionForms.contact),
		mode: "onChange",
		defaultValues: {
			note: "",
			review_at: djs()
				.tz("America/Sao_Paulo")
				.add(1, "day")
				.hour(9)
				.minute(0)
				.format("YYYY-MM-DDTHH:mm"),
		},
	});

	return (
		<DecisionForm
			action={action}
			kind="contact"
			onClose={onClose}
			onSubmit={form.handleSubmit((input) =>
				mutate({
					id: action.id,
					version: action.version,
					note: input.note,
					next_review_at: djs.tz(input.review_at, "America/Sao_Paulo").toDate(),
				}),
			)}
			isPending={isPending}
			isValid={form.formState.isValid}
			result={data}
		>
			<DecisionNote
				registration={form.register("note")}
				label="o que aconteceu no contato?"
				isPending={isPending}
			/>
			<DecisionReview registration={form.register("review_at")} isPending={isPending} />
		</DecisionForm>
	);
}

function WaitDecision({ action, onClose }: ActionDecisionProps) {
	const { mutate, isPending, data } = useMutation(
		orpc.today.wait.mutationOptions(useActionDecision(action, onClose)),
	);
	const form = useForm<typeof ActionDecisionForms.wait.infer>({
		resolver: arktypeResolver(ActionDecisionForms.wait),
		mode: "onChange",
		defaultValues: {
			note: action.waiting_reason ?? "",
			waiting_for: action.waiting_for ?? "patient",
			review_at: djs()
				.tz("America/Sao_Paulo")
				.add(1, "day")
				.hour(9)
				.minute(0)
				.format("YYYY-MM-DDTHH:mm"),
		},
	});

	return (
		<DecisionForm
			action={action}
			kind="wait"
			onClose={onClose}
			onSubmit={form.handleSubmit((input) =>
				mutate({
					id: action.id,
					version: action.version,
					note: input.note,
					waiting_for: input.waiting_for,
					next_review_at: djs.tz(input.review_at, "America/Sao_Paulo").toDate(),
				}),
			)}
			isPending={isPending}
			isValid={form.formState.isValid}
			result={data}
		>
			<label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
				aguardando
				<Select {...form.register("waiting_for")} aria-label="aguardando" disabled={isPending}>
					{Object.entries(ActionQueueLabels.waitingFor).map(([value, label]) => (
						<option key={value} value={value}>
							{label}
						</option>
					))}
				</Select>
			</label>
			<DecisionNote
				registration={form.register("note")}
				label="o que falta acontecer?"
				isPending={isPending}
			/>
			<DecisionReview registration={form.register("review_at")} isPending={isPending} />
		</DecisionForm>
	);
}

const nextRole = { medica: "secretaria", secretaria: "medica" } as const;

function TransferDecision({ action, onClose }: ActionDecisionProps) {
	const { mutate, isPending, data } = useMutation(
		orpc.today.transfer.mutationOptions(useActionDecision(action, onClose)),
	);
	const form = useForm<typeof ActionDecisionForms.transfer.infer>({
		resolver: arktypeResolver(ActionDecisionForms.transfer),
		mode: "onChange",
		defaultValues: { note: "", role: nextRole[action.role] },
	});

	return (
		<DecisionForm
			action={action}
			kind="transfer"
			onClose={onClose}
			onSubmit={form.handleSubmit((input) =>
				mutate({
					id: action.id,
					version: action.version,
					note: input.note,
					role: input.role,
				}),
			)}
			isPending={isPending}
			isValid={form.formState.isValid}
			result={data}
		>
			<label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
				responsável
				<Select {...form.register("role")} aria-label="responsável" disabled={isPending}>
					<option value="medica" disabled={action.role === "medica"}>
						médica
					</option>
					<option value="secretaria" disabled={action.role === "secretaria"}>
						secretária
					</option>
				</Select>
			</label>
			<DecisionNote
				registration={form.register("note")}
				label="qual resultado falta?"
				isPending={isPending}
			/>
		</DecisionForm>
	);
}

function ResumeDecision({ action, onClose }: ActionDecisionProps) {
	const { mutate, isPending, data } = useMutation(
		orpc.today.resume.mutationOptions(useActionDecision(action, onClose)),
	);
	const form = useForm<typeof ActionDecisionForms.note.infer>({
		resolver: arktypeResolver(ActionDecisionForms.note),
		mode: "onChange",
		defaultValues: { note: "" },
	});

	return (
		<DecisionForm
			action={action}
			kind="resume"
			onClose={onClose}
			onSubmit={form.handleSubmit((input) =>
				mutate({ id: action.id, version: action.version, note: input.note }),
			)}
			isPending={isPending}
			isValid={form.formState.isValid}
			result={data}
		>
			<DecisionNote
				registration={form.register("note")}
				label="o que mudou para retomar?"
				isPending={isPending}
			/>
		</DecisionForm>
	);
}

function ContestDecision({ action, onClose }: ActionDecisionProps) {
	const { mutate, isPending, data } = useMutation(
		orpc.today.contest.mutationOptions(useActionDecision(action, onClose)),
	);
	const form = useForm<typeof ActionDecisionForms.contest.infer>({
		resolver: arktypeResolver(ActionDecisionForms.contest),
		mode: "onChange",
		defaultValues: { reason: "" },
	});

	return (
		<DecisionForm
			action={action}
			kind="contest"
			onClose={onClose}
			onSubmit={form.handleSubmit((input) =>
				mutate({ id: action.id, version: action.version, reason: input.reason }),
			)}
			isPending={isPending}
			isValid={form.formState.isValid}
			result={data}
		>
			<DecisionNote
				registration={form.register("reason")}
				label="o que está incorreto?"
				isPending={isPending}
			/>
		</DecisionForm>
	);
}
